/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { EventHandlerMap } from '../../@types/relations';
import type { XHubChatClient } from '../../client';
import type { XHubChatEvent } from '../event/event';
import { EventStatus, RelationType, XHubChatEventEvent } from '../../@types/event';
import { RelationsEvent } from '../../@types/relations';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';
import { logger } from '../../utils/logger';
import { Room } from '../room/room';

const matchesEventType = (eventType: string, targetEventType: string, altTargetEventTypes: string[] = []): boolean =>
  [targetEventType, ...altTargetEventTypes].includes(eventType);

/**
 * A container for relation events that supports easy access to common ways of
 * aggregating such events. Each instance holds events that of a single relation
 * type and event type. All of the events also relate to the same original event.
 *
 * The typical way to get one of these containers is via
 * EventTimelineSet#getRelationsForEvent.
 */
export class Relations extends TypedEventEmitter<RelationsEvent, EventHandlerMap> {
  private readonly client: XHubChatClient;
  private targetEvent: XHubChatEvent | null = null;
  private relations = new Set<XHubChatEvent>();
  private relationEventIds = new Set<string>();

  private annotationsByKey: Record<string, Set<XHubChatEvent>> = {};
  private annotationsBySender: Record<string, Set<XHubChatEvent>> = {};
  private sortedAnnotationsByKey: [string, Set<XHubChatEvent>][] = [];

  private creationEmitted = false;

  /**
   * @param relationType - The type of relation involved, such as "m.annotation", "m.reference", "m.replace", etc.
   * @param eventType - The relation event's type, such as "m.reaction", etc.
   * @param client - The client which created this instance. For backwards compatibility also accepts a Room.
   * @param altEventTypes - alt event types for relation events, for example to support unstable prefixed event types
   */
  public constructor(
    public readonly relationType: RelationType | string,
    public readonly eventType: string,
    client: XHubChatClient | Room,
    public readonly altEventTypes?: string[],
  ) {
    super();
    this.client = client instanceof Room ? client.client : client;
  }

  /**
   * ======================================================================
   *                            GETTERS
   * ======================================================================
   */

  /**
   * Get all relation events in this collection.
   *
   * These are currently in the order of insertion to this collection, which
   * won't match timeline order in the case of scrollback.
   * TODO: Tweak `addEvent` to insert correctly for scrollback.
   *
   * Relation events in insertion order.
   */
  public getRelations(): XHubChatEvent[] {
    return [...this.relations];
  }

  /**
   * ======================================================================
   *                        END OF GETTERS
   * ======================================================================
   */

  /**
   * ======================================================================
   *                            SETTERS
   * ======================================================================
   */
  /*
     * @param targetEvent - the event the relations are related to.
     */
  public async setTargetEvent(event: XHubChatEvent): Promise<void> {
    if (this.targetEvent) {
      return;
    }
    this.targetEvent = event;

    this.maybeEmitCreated();
  }
  /**
   * ======================================================================
   *                        END OF SETTERS
   * ======================================================================
   */

  /**
   * Listens for event status changes to remove cancelled events.
   *
   * @param event - The event whose status has changed
   * @param status - The new status
   */
  private onEventStatus = (event: XHubChatEvent, status: EventStatus | null): void => {
    if (!event.isSending()) {
      // Sending is done, so we don't need to listen anymore
      event.removeListener(XHubChatEventEvent.Status, this.onEventStatus);
      return;
    }
    if (status !== EventStatus.CANCELLED) {
      return;
    }
    // Event was cancelled, remove from the collection
    event.removeListener(XHubChatEventEvent.Status, this.onEventStatus);
    this.removeEvent(event);
  };

  private addAnnotationToAggregation(event: XHubChatEvent): void {
    const { key } = event.getRelation() ?? {};
    if (!key) return;

    let eventsForKey = this.annotationsByKey[key];
    if (!eventsForKey) {
      eventsForKey = this.annotationsByKey[key] = new Set();
      this.sortedAnnotationsByKey.push([key, eventsForKey]);
    }
    // Add the new event to the set for this key
    eventsForKey.add(event);
    // Re-sort the [key, events] pairs in descending order of event count
    this.sortedAnnotationsByKey.sort((a, b) => {
      const aEvents = a[1];
      const bEvents = b[1];
      return bEvents.size - aEvents.size;
    });

    const sender = event.getSender()!;
    let eventsFromSender = this.annotationsBySender[sender];
    if (!eventsFromSender) {
      eventsFromSender = this.annotationsBySender[sender] = new Set();
    }
    // Add the new event to the set for this sender
    eventsFromSender.add(event);
  }

  /**
   * Add relation events to this collection.
   *
   * @param event - The new relation event to be added.
   */
  public async addEvent(event: XHubChatEvent): Promise<void> {
    if (this.relationEventIds.has(event.getId()!)) {
      return;
    }

    const relation = event.getRelation();
    if (!relation) {
      logger.error('Event must have relation info');
      return;
    }

    const relationType = relation.rel_type;
    const eventType = event.getType();

    if (this.relationType !== relationType || !matchesEventType(eventType, this.eventType, this.altEventTypes)) {
      logger.error('Event relation info doesn\'t match this container');
      return;
    }

    // If the event is in the process of being sent, listen for cancellation
    // so we can remove the event from the collection.
    if (event.isSending()) {
      event.on(XHubChatEventEvent.Status, this.onEventStatus);
    }

    this.relations.add(event);
    this.relationEventIds.add(event.getId()!);

    if (this.relationType === RelationType.Annotation) {
      this.addAnnotationToAggregation(event);
    }
    event.on(XHubChatEventEvent.BeforeRedaction, this.onBeforeRedaction);

    this.emit(RelationsEvent.Add, event);

    this.maybeEmitCreated();
  }

  /**
   * For relations that have been redacted, we want to remove them from
   * aggregation data sets and emit an update event.
   *
   * To do so, we listen for `Event.beforeRedaction`, which happens:
   *   - after the server accepted the redaction and remote echoed back to us
   *   - before the original event has been marked redacted in the client
   *
   * @param redactedEvent - The original relation event that is about to be redacted.
   */
  private onBeforeRedaction = async (redactedEvent: XHubChatEvent): Promise<void> => {
    if (!this.relations.has(redactedEvent)) {
      return;
    }

    this.relations.delete(redactedEvent);

    if (this.relationType === RelationType.Annotation) {
      // Remove the redacted annotation from aggregation by key
      this.removeAnnotationFromAggregation(redactedEvent);
    }

    redactedEvent.removeListener(XHubChatEventEvent.BeforeRedaction, this.onBeforeRedaction);

    this.emit(RelationsEvent.Redaction, redactedEvent);
  };

  /**
   * Get all events in this collection grouped by key and sorted by descending
   * event count in each group.
   *
   * This is currently only supported for the annotation relation type.
   *
   * An array of [key, events] pairs sorted by descending event count.
   * The events are stored in a Set (which preserves insertion order).
   */
  public getSortedAnnotationsByKey(): [string, Set<XHubChatEvent>][] | null {
    if (this.relationType !== RelationType.Annotation) {
      // Other relation types are not grouped currently.
      return null;
    }

    return this.sortedAnnotationsByKey;
  }

  /**
   * Get all events in this collection grouped by sender.
   *
   * This is currently only supported for the annotation relation type.
   *
   * An object with each relation sender as a key and the matching Set of
   * events for that sender as a value.
   */
  public getAnnotationsBySender(): Record<string, Set<XHubChatEvent>> | null {
    if (this.relationType !== RelationType.Annotation) {
      // Other relation types are not grouped currently.
      return null;
    }

    return this.annotationsBySender;
  }

  private removeAnnotationFromAggregation(event: XHubChatEvent): void {
    const { key } = event.getRelation() ?? {};
    if (!key) return;

    const eventsForKey = this.annotationsByKey[key];
    if (eventsForKey) {
      eventsForKey.delete(event);

      // Re-sort the [key, events] pairs in descending order of event count
      this.sortedAnnotationsByKey.sort((a, b) => {
        const aEvents = a[1];
        const bEvents = b[1];
        return bEvents.size - aEvents.size;
      });
    }

    const sender = event.getSender()!;
    const eventsFromSender = this.annotationsBySender[sender];
    if (eventsFromSender) {
      eventsFromSender.delete(event);
    }
  }

  /**
   * Remove relation event from this collection.
   * @param event - The relation event to remove.
   */
  public async removeEvent(event: XHubChatEvent): Promise<void> {
    if (!this.relations.has(event)) {
      return;
    }

    this.relations.delete(event);

    if (this.relationType === RelationType.Annotation) {
      this.removeAnnotationFromAggregation(event);
    }

    this.emit(RelationsEvent.Remove, event);
  }

  private maybeEmitCreated(): void {
    if (this.creationEmitted) {
      return;
    }
    // Only emit we're "created" once we have a target event instance _and_
    // at least one related event.
    if (!this.targetEvent || !this.relations.size) {
      return;
    }
    this.creationEmitted = true;
    this.targetEvent.emit(XHubChatEventEvent.RelationsCreated, this.relationType, this.eventType);
  }
}
